var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import Condition from "../core/condition";
import Hero from "../renderer/types/hero";
import Animation from "../renderer/types/animation";
import BlobType from "../renderer/types/blob";
import FontSheet from "../renderer/types/font-sheet";
import { resolve } from "url";
import { Slide } from "../scenes/canvas-scene";
export default class Assembler {
    constructor(assetsManager) {
        this.cache = {};
        this.pendingLoad = {};
        this.responses = {};
        this.filesLoaded = {};
        this.assetsManager = assetsManager;
    }
    blobToBase64(blob) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve) => {
                const reader = new FileReader();
                reader.readAsDataURL(blob);
                reader.addEventListener("loadend", () => resolve(reader.result));
            });
        });
    }
    loadImage(blob) {
        return __awaiter(this, void 0, void 0, function* () {
            return new Promise((resolve) => {
                const image = new Image();
                image.addEventListener("load", () => {
                    resolve(image);
                });
                image.src = URL.createObjectURL(blob);
            });
        });
    }
    internalFetch(longpath, transform) {
        return __awaiter(this, void 0, void 0, function* () {
            const path = resolve(location.href, longpath);
            return new Promise((resolve) => {
                if (this.responses[path]) {
                    resolve(this.responses[path]);
                    return;
                }
                if (!this.pendingLoad[path]) {
                    this.pendingLoad[path] = [];
                    fetch(path).then(response => transform(response)).then((result) => {
                        this.responses[path] = result;
                        this.pendingLoad[path].forEach(callback => callback(result));
                        delete this.pendingLoad[path];
                        this.filesLoaded[path.split(location.href)[1]] = result;
                    });
                }
                this.pendingLoad[path].push(result => {
                    resolve(result);
                });
            });
        });
    }
    fetchWithCache(path) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!this.cache[path]) {
                const result = yield this.internalFetch(path, response => {
                    return response.blob()
                        .then(blob => this.blobToBase64(blob)
                        .then(base64 => ({
                        blob, base64: base64 !== null && base64 !== void 0 ? base64 : ""
                    }))
                        .then(({ blob, base64 }) => this.loadImage(blob).then(image => ({
                        blob, base64, image,
                    }))));
                });
                this.cache[path] = result;
            }
            return this.cache[path];
        });
    }
    paramsReplacement(obj, params) {
        if (!params) {
            return obj;
        }
        if (typeof (obj) === "string" && typeof (params[obj]) !== "undefined") {
            return params[obj];
        }
        if (typeof (obj) !== 'object' || !obj) {
            return obj;
        }
        if (Array.isArray(obj)) {
            for (let i = 0; i < obj.length; i++) {
                obj[i] = this.paramsReplacement(obj[i], params);
            }
        }
        else {
            for (const i in obj) {
                if (obj.hasOwnProperty(i)) {
                    obj[i] = this.paramsReplacement(obj[i], params);
                }
            }
        }
        return obj;
    }
    assemble(obj, dir, property = "") {
        return __awaiter(this, void 0, void 0, function* () {
            if (typeof (obj) !== 'object' || !obj) {
                return obj;
            }
            if (Array.isArray(obj)) {
                for (let i = 0; i < obj.length; i++) {
                    obj[i] = yield this.assemble(obj[i], dir, `${property}[${i}]`);
                }
            }
            else {
                for (const i in obj) {
                    if (obj.hasOwnProperty(i)) {
                        obj[i] = yield this.assemble(obj[i], dir, `${property}.${i}`);
                    }
                }
            }
            if ((obj.type === "reference" || obj.type === "ref") && obj.path) {
                return yield this.load(`${dir !== null && dir !== void 0 ? dir : ''}${obj.path}`, obj, property);
            }
            if (obj.type === "text" && obj.path) {
                return yield this.loadText(`${dir !== null && dir !== void 0 ? dir : ''}${obj.path}`, obj, property);
            }
            if (obj.type === "image" && obj.path) {
                const { blob, base64, image } = yield this.fetchWithCache(`${dir}${obj.path}`);
                //      this.filesLoaded[`${dir}${obj.path}`] = blob;
                this.assetsManager[obj.path] = base64;
                return new BlobType({
                    path: obj.path,
                    blob,
                    image,
                });
            }
            if (obj.type === "music" && obj.path && !obj.pathFixed) {
                return Object.assign(Object.assign({}, obj), { path: `${dir !== null && dir !== void 0 ? dir : ''}${obj.path}`, pathFixed: true });
            }
            if (obj.type === "animation") {
                return new Animation(obj);
            }
            if (obj.type === "fontSheet") {
                return new FontSheet(obj);
            }
            if (obj.type === "condition") {
                return new Condition(obj);
            }
            if (obj.type === "slide") {
                return new Slide(obj);
            }
            if (obj.type === "hero") {
                return new Hero(obj);
            }
            return obj;
        });
    }
    load(path, obj, property) {
        return __awaiter(this, void 0, void 0, function* () {
            // console.log(">>", path, property);
            const dir = path.substring(0, path.lastIndexOf("/") + 1);
            const result = yield this.internalFetch(path, response => response.json());
            const json = JSON.parse(JSON.stringify(result));
            const replacedJson = this.paramsReplacement(json, obj.params);
            return yield this.assemble(replacedJson, dir, property);
        });
    }
    loadText(path, { splitLines, splitCells }, property) {
        return __awaiter(this, void 0, void 0, function* () {
            // console.log(">>", path, property);
            const text = yield this.internalFetch(path, response => response.text());
            return splitLines || splitCells ? text.split("\n").map(line => splitCells ? line.match(/.{1,2}/g) : line) : text;
        });
    }
}
